import type { SyncEntityKey } from './localStorageService';

export const SYNC_MANIFEST_FILE_NAME = 'sync-manifest-v2.json';
export const SYNC_MANIFEST_SCHEMA_VERSION = 2;

export interface SyncManifestEntry {
  revision: number;
  contentHash: string;
  modifiedAt: string;
  deviceId: string;
  fileId: string;
  deleted?: boolean;
}

export interface SyncManifest {
  schemaVersion: typeof SYNC_MANIFEST_SCHEMA_VERSION;
  revision: number;
  entities: Partial<Record<SyncEntityKey, SyncManifestEntry>>;
}

export interface SyncEntityConflict {
  entityKey: SyncEntityKey;
  localHash: string;
  localModifiedAt: string;
  remote: SyncManifestEntry;
}

export type ReconciliationDecision =
  | 'upload'
  | 'download'
  | 'synchronized'
  | 'conflict';

export interface ReconciliationState {
  localExists: boolean;
  localDirty: boolean;
  localHash: string | null;
  baseRevision: number | null;
  remote: SyncManifestEntry | null;
}

export function decideReconciliation(state: ReconciliationState): ReconciliationDecision {
  const { localExists, localDirty, localHash, baseRevision, remote } = state;

  if (!remote) return localExists ? 'upload' : 'synchronized';
  if (remote.deleted && !localExists && !localDirty) return 'synchronized';
  if (localHash === remote.contentHash && localExists !== Boolean(remote.deleted)) {
    return 'synchronized';
  }
  if (!localDirty) return 'download';
  if (baseRevision === remote.revision) return 'upload';
  return 'conflict';
}

export function createEmptyManifest(): SyncManifest {
  return {
    schemaVersion: SYNC_MANIFEST_SCHEMA_VERSION,
    revision: 0,
    entities: {},
  };
}

export function stableContentHash(value: unknown): string {
  const input = JSON.stringify(value, (_, nestedValue) => {
    if (!nestedValue || typeof nestedValue !== 'object' || Array.isArray(nestedValue)) {
      return nestedValue;
    }
    return Object.fromEntries(
      Object.entries(nestedValue as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
    );
  });

  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}